/**
 * Runs one workflow action on many items: one transition per item, three at a
 * time, through the same endpoint as the single action. There is no server
 * batch route; each transition writes its own audit row.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { WorkflowItemsService } from "@/api/services/WorkflowItemsService";
import { isWorkflowAlreadyHandled, workflowKeys } from "@/hooks/useWorkflows";
import { mapLimited } from "@/lib/mapLimited";

const CONCURRENCY = 3;

export interface BatchTransitionSummary {
  /** The items this run moved. */
  done: string[];
  /** The items whose transition failed. */
  failed: string[];
  /** The items someone else handled first (409/422/404): not a problem. */
  alreadyHandled: string[];
}

/** Fires `action` on every item and reports the outcome per item, in input order. */
export function useBatchTransition(action: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]): Promise<BatchTransitionSummary> => {
      const done = new Set<string>();
      const failed = new Set<string>();
      const alreadyHandled = new Set<string>();
      await mapLimited(ids, CONCURRENCY, async (id) => {
        try {
          await WorkflowItemsService.postApiV1WorkflowItemsTransition(id, { action });
          done.add(id);
        } catch (error) {
          if (isWorkflowAlreadyHandled(error)) alreadyHandled.add(id);
          else failed.add(id);
        }
      });
      return {
        done: ids.filter((id) => done.has(id)),
        failed: ids.filter((id) => failed.has(id)),
        alreadyHandled: ids.filter((id) => alreadyHandled.has(id)),
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.details() });
    },
  });
}
